import {
  createContext,
  useContext,
  useCallback,
  useMemo,
  type ReactNode,
} from "react";

/** Routing owns the readable reference; data and permissions use roundId. */
import { roundHref, type ResolvedRoundRoute } from "./roundRoutePaths";
export type { ResolvedRoundRoute } from "./roundRoutePaths";
const RoundRouteContext = createContext<ResolvedRoundRoute | null>(null);

export function RoundRouteProvider({
  value,
  children,
}: {
  value: ResolvedRoundRoute;
  children: ReactNode;
}) {
  const { roundId, reference } = value;
  const stableValue = useMemo(
    () => ({ roundId, reference }),
    [roundId, reference],
  );
  return (
    <RoundRouteContext.Provider value={stableValue}>
      {children}
    </RoundRouteContext.Provider>
  );
}

export function useOptionalRoundRoute() {
  return useContext(RoundRouteContext);
}

export function useRoundRoute() {
  const route = useOptionalRoundRoute();

  if (!route) {
    throw new Error("A resolved round route is required");
  }

  return route;
}

export function useRoundHref() {
  const route = useOptionalRoundRoute();
  return useCallback((path: string) => roundHref(path, route), [route]);
}
